import { cn } from "@/components/podkit/lib/cn";
import { formatRepoUrlForDisplay } from "@/format/url";
import { getRepoUrlFromInitializer } from "@/queries/environment-queries";
import type { PlainOrganizationMember } from "@/queries/organization-queries";
import type { PlainProject } from "@/queries/project-queries";
import { CreateEnvironmentButton } from "@/routes/environments/create/CreateEnvironmentButton";
import { getProjectUsers } from "@/utils/projects";
import { useMemo, type FC } from "react";
import { AvatarStack } from "@/components/projects/cards/AvatarStack";
import { userToStackItem } from "@/components/projects/cards/avatar-stack-helpers";

type Props = {
    project: PlainProject;
    orgMembers?: PlainOrganizationMember[];
    onCreateSuccess?: () => void;
};
export const CompactProjectCard: FC<Props> = ({ project, orgMembers, onCreateSuccess }) => {
    const projectContextUrl = getRepoUrlFromInitializer(project.initializer);
    const users = getProjectUsers(project, orgMembers);

    const avatarStackItems = useMemo(() => {
        return users?.map(userToStackItem) ?? [];
    }, [users]);

    return (
        <CreateEnvironmentButton
            className={cn(
                "group/card flex h-auto w-full flex-row gap-2 rounded-xl border border-solid border-border-light bg-surface-glass px-6 py-3 text-left transition-shadow duration-200 @container hover:shadow",
                "hover:bg-surface-primary",
            )}
            project={project}
            onCreateSuccess={onCreateSuccess}
            hideTooltip={true}
            aria-label={`Create from project: ${project.metadata?.name}`}
        >
            <div className="min-w-0 grow overflow-hidden">
                <div
                    className="truncate text-lg font-medium leading-6 text-content-primary"
                    title={project.metadata?.name}
                >
                    {project.metadata?.name}
                </div>
                <div className="truncate text-base leading-5 text-content-secondary">
                    {formatRepoUrlForDisplay(projectContextUrl)}
                </div>
            </div>
            <div className="flex min-w-0 shrink-0 flex-col justify-center">
                <AvatarStack items={avatarStackItems} disablePopover={true} />
            </div>
        </CreateEnvironmentButton>
    );
};
